<template>
    <div class="listener-discover">
        <section class="listener-hero">
            <div class="listener-hero__top">
                <div>
                    <div class="listener-hero__eyebrow">{{ $tt('Music') }}</div>
                    <h1 class="listener-hero__title">{{ $tt('为你打开今天的旋律') }}</h1>
                </div>
                <div class="listener-hero__actions">
                    <button class="listener-search-button" @click="redirectToSearch">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <circle cx="11" cy="11" r="7"></circle>
                            <path d="m20 20-3.5-3.5"></path>
                        </svg>
                    </button>
                    <button class="listener-search-button" @click="redirectToAccount">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <path d="M12 12a4 4 0 1 0-4-4 4 4 0 0 0 4 4z"></path>
                            <path d="M4 20a8 8 0 0 1 16 0"></path>
                        </svg>
                    </button>
                </div>
            </div>
        
            <div
                class="listener-hero__card"
                :style="heroBackgroundStyle"
                @click="playHeroTrack"
            >
                <div class="listener-hero__overlay"></div>
                <div class="listener-hero__content">
                    <div class="listener-hero__badge">{{ hotTrack.artist_name || $tt('精选推荐') }}</div>
                    <div class="listener-hero__track">{{ hotTrack.name || $tt('随机播放') }}</div>
                    <div class="listener-hero__sub">
                        {{ $tt('已被播放 {count} 次', { count: formatNumber(hotTrack.play_count || 0) }) }}
                    </div>
                </div>
                <button class="listener-hero__play" @click.stop="playHeroTrack">
                    <svg viewBox="0 0 24 24" fill="currentColor">
                        <path d="M8 5.14v13.72a1 1 0 0 0 1.5.86l10-6.86a1 1 0 0 0 0-1.72l-10-6.86A1 1 0 0 0 8 5.14z" />
                    </svg>
                </button>
            </div>
        </section>
        
        <section class="listener-section">
            <div class="listener-section__header">
                <h2>{{ $tt('播放列表') }}</h2>
                <button @click="redirectToPlaylists">{{ $tt('全部') }}</button>
            </div>
            <div class="listener-playlist-shortcuts">
                <button class="listener-playlist-shortcuts__hero" @click="redirectToPlaylists">
                    <div class="listener-playlist-shortcuts__hero-copy">
                        <div class="listener-playlist-shortcuts__eyebrow">{{ $tt('Playlist') }}</div>
                        <div class="listener-playlist-shortcuts__title">{{ $tt('把常听的旋律放进一份清单') }}</div>
                        <div class="listener-playlist-shortcuts__sub">{{ $tt('最近添加、A-Z 排序都能从这里直接进入。') }}</div>
                    </div>
                    <div class="listener-playlist-shortcuts__hero-icon">
                        <svg viewBox="0 0 24 24" fill="currentColor">
                            <path d="M5 7.5h14M5 12h14M5 16.5h8"></path>
                            <path d="M17 16.25V10.5l4 2.875z"></path>
                        </svg>
                    </div>
                </button>
                <div class="listener-playlist-shortcuts__grid">
                    <button class="listener-playlist-shortcuts__card" @click="redirectToPlaylists('recently-added')">
                        <div class="listener-playlist-shortcuts__card-title">{{ $tt('最近添加') }}</div>
                        <div class="listener-playlist-shortcuts__card-sub">{{ $tt('先看刚整理好的歌单') }}</div>
                    </button>
                    <button class="listener-playlist-shortcuts__card" @click="redirectToPlaylists('a-z')">
                        <div class="listener-playlist-shortcuts__card-title">{{ $tt('A-Z 排序') }}</div>
                        <div class="listener-playlist-shortcuts__card-sub">{{ $tt('按名字快速找到目标') }}</div>
                    </button>
                </div>
            </div>
        </section>
        
        <section class="listener-section">
            <div class="listener-section__header">
                <h2>{{ $tt('新专辑') }}</h2>
                <button @click="redirectToAlbums">{{ $tt('全部') }}</button>
            </div>
            <div class="listener-album-grid">
                <div
                    v-for="albumItem in mobileAlbums"
                    :key="albumItem.id"
                    class="listener-album-card"
                    @click="redirectToAlbum(albumItem.id)"
                >
                    <img
                        :src="albumCoverUrl(albumItem)"
                        :alt="albumItem.name"
                        class="listener-album-card__image"
                    >
                    <div class="listener-album-card__title">{{ albumItem.name }}</div>
                    <div class="listener-album-card__artist">{{ albumItem.artist_name || albumItem.artist }}</div>
                </div>
            </div>
        </section>
        
        <section class="listener-section">
            <div class="listener-section__header">
                <h2>{{ $tt('热门艺术家') }}</h2>
                <button @click="redirectToArtists">{{ $tt('全部') }}</button>
            </div>
            <div class="listener-artist-strip">
                <div
                    v-for="artist in mobileArtists"
                    :key="artist.id"
                    class="listener-artist-card"
                    @click="redirectToArtist(artist.id)"
                >
                    <img
                        :src="artistCoverUrl(artist)"
                        :alt="artist.name"
                        class="listener-artist-card__image"
                    >
                    <div class="listener-artist-card__name">{{ artist.name }}</div>
                </div>
            </div>
        </section>
        
        <section class="listener-section">
            <div class="listener-section__header">
                <h2>{{ $tt('风格浏览') }}</h2>
                <button @click="redirectToGenres">{{ $tt('全部') }}</button>
            </div>
            <div class="listener-genre-strip">
                <button
                    v-for="genre in mobileGenres"
                    :key="genre.id"
                    class="listener-genre-card"
                    @click="redirectToGenre(genre.id)"
                >
                    <div class="listener-genre-card__name">{{ genre.name }}</div>
                    <div class="listener-genre-card__sub">{{ $tt('{count} 首歌曲', { count: genre.trackCount || 0 }) }}</div>
                </button>
            </div>
        </section>
        
        <section class="listener-section listener-track-list">
            <div class="listener-section__header">
                <h2>{{ $tt('歌曲列表') }}</h2>
                <button @click="redirectToHistory">{{ $tt('查看更多') }}</button>
            </div>
            <div
                v-for="(song, index) in mobileTracks"
                :key="song.id || `song-${index}`"
                class="listener-track-row"
                @click="play(index)"
            >
                <div class="listener-track-row__cover">
                    <img
                        :src="song.image || '/static/dist/img/album_null.jpg'"
                        :alt="song.title"
                    >
                </div>
                <div class="listener-track-row__meta">
                    <div class="listener-track-row__title truncate">{{ song.title }}</div>
                    <div class="listener-track-row__artist truncate">{{ song.artist }}</div>
                </div>
                <div class="listener-track-row__side">
                    <div class="listener-track-row__duration">{{ formatDuration(song.duration) }}</div>
                    <button
                        class="listener-track-row__more"
                        @click.stop="toggleFavouriteTrack(song)"
                    >
                        <svg viewBox="0 0 24 24" :fill="song.favourite ? '#ef4444' : 'none'" stroke="currentColor" stroke-width="2">
                            <path d="M12 21s-6.72-4.35-9.2-8.3A5.38 5.38 0 0 1 7.35 4.6 5.2 5.2 0 0 1 12 7.09 5.2 5.2 0 0 1 16.65 4.6a5.38 5.38 0 0 1 4.55 8.1C18.72 16.65 12 21 12 21z"></path>
                        </svg>
                    </button>
                </div>
            </div>
        </section>
    </div>
</template>
<script>
import { getCoverArtUrl, normalizeTrack } from '@/api/apiUrl/subsonic/serializers'

export default {
    name: 'ListenerDiscover',
    data() {
        return {
            discover: [],
            randomSongs: [],
            hotArtists: [],
            similarArtists: [],
            album: {},
            recommendAlbums: [],
            recentPlays: [],
            newAlbums: [],
            hotTrack: {},
            mobileGenreItems: []
        }
    },
    computed: {
        isPlaying() {
            return this.$store.state.common.isPlaying
        },
        tracks() {
            return this.$store.state.common.queue
        },
        queueIndex() {
            return this.$store.state.common.queueIndex
        },
        heroBackgroundStyle() {
            const backgroundUrl = this.hotTrack.cover
                ? this.callCoverArtUrl(this.hotTrack.cover)
                : (this.randomSongs[0] && this.randomSongs[0].image) || '/static/dist/img/album_null.jpg'
            return {
                backgroundImage: `linear-gradient(135deg, rgba(15, 23, 42, 0.18), rgba(17, 24, 39, 0.62)), url(${backgroundUrl})`
            }
        },
        mobileAlbums() {
            return (this.recommendAlbums && this.recommendAlbums.length > 0
                ? this.recommendAlbums
                : this.newAlbums).slice(0, 3)
        },
        mobileArtists() {
            return this.hotArtists.slice(0, 3)
        },
        mobileGenres() {
            return this.mobileGenreItems.slice(0, 4)
        },
        mobileTracks() {
            return this.randomSongs.slice(0, 6)
        }
    },
    created() {
        this.fetchRandomSongs()
        this.fetchHotArtists()
        this.fetchMobileGenres()
    },
    methods: {
        callCoverArtUrl(coverArt) {
            const url = getCoverArtUrl({ coverArt })
            console.log(url)
            if (url) {
                return url
            } else {
                return '/static/dist/img/album_null.jpg'
            }
        },
        dragstart(id, event) {
            event.dataTransfer.setData('application/x-track-id', id)
        },
        remove(idx) {
            return this.$store.commit('removeFromQueue', idx)
        },
        clear() {
            return this.$store.commit('clearQueue')
        },
        shuffle() {
            return this.$store.commit('shuffleQueue')
        },
        formatDuration(value) {
            if (!isFinite(value)) {
                return '∞'
            }
            const minutes = Math.floor(value / 60)
            const seconds = Math.floor(value % 60)
            return (minutes < 10 ? '0' : '') + minutes + ':' + (seconds < 10 ? '0' : '') + seconds
        },
        async playNow(id) {
            const playlist = await this.$api.subsonic.getPlaylist(id)
            return this.$store.dispatch('playTrackList', {
                tracks: playlist.tracks
            })
        },
        albumCoverUrl(albumItem) {
            if (albumItem.image) return albumItem.image
            return this.callCoverArtUrl(albumItem.cover || albumItem.coverArt)
        },
        artistCoverUrl(artist) {
            return this.callCoverArtUrl(artist.cover || artist.coverArt)
        },
        fetchDiscover() {
            return this.$api.subsonic.getDiscover().then((res) => {
                this.discover = res.discover
            })
        },
        async fetchRandomSongs() {
            try {
                const response = await this.$api.subsonic.getRandomSongs(8)
                this.randomSongs = response || []
                console.log(this.randomSongs)
            } catch (error) {
                console.error('获取随机歌曲失败:', error)
                this.randomSongs = []
            }
        },
        formatNumber(num) {
            if (num >= 1000000) {
                return (num / 1000000).toFixed(1) + 'M'
            } else if (num >= 1000) {
                return (num / 1000).toFixed(1) + 'K'
            }
            return num
        },
        async fetchHotArtists() {
            try {
                const response = await this.$api.Task.getHotArtists()
                let hotArtists = response.data.hot_artists || []

                // 如果 hotArtists 少于 6 条，用最后一条数据重复填充到 6 条
                if (hotArtists.length > 0 && hotArtists.length < 6) {
                    const lastArtist = hotArtists[hotArtists.length - 1]
                    while (hotArtists.length < 6) {
                        hotArtists.push({ ...lastArtist })
                    }
                }

                this.hotArtists = hotArtists
                this.similarArtists = response.data.similar_artists || []
                this.album = response.data.album || {}
                this.recommendAlbums = response.data.recommend_albums || []
                this.recentPlays = response.data.recent_plays || []
                this.newAlbums = response.data.new_albums || []
                this.hotTrack = response.data.hot_track || {}
            } catch (error) {
                console.error('获取热门艺术家失败:', error)
                this.hotArtists = []
                this.similarArtists = []
                this.album = {}
                this.recommendAlbums = []
                this.recentPlays = []
                this.newAlbums = []
                this.hotTrack = {}
            }
        },
        async fetchMobileGenres() {
            try {
                this.mobileGenreItems = await this.$api.subsonic.getGenres('most-tracks')
            } catch (error) {
                console.error('获取风格失败:', error)
                this.mobileGenreItems = []
            }
        },
        redirectToArtist(id) {
            this.$router.push({ name: 'artist', params: { id: id } })
        },
        redirectToArtists() {
            this.$router.push({path: '/artists/most-albums/'})
        },
        redirectToGenre(id) {
            this.$router.push({ name: 'genre', params: { id } })
        },
        redirectToGenres() {
            this.$router.push({ path: '/genres/a-z/' })
        },
        redirectToAlbum(id) {
            this.$router.push({ name: 'album', params: { id: id } })
        },
        async playAlbumNow(id) {
            const album = await this.$api.subsonic.getAlbumDetails(id)
            return this.$store.dispatch('playTrackList', {
                tracks: album.tracks
            })
        },
        async playHeroTrack() {
            if (this.hotTrack && this.hotTrack.id) {
                const song = await this.$api.subsonic.getSong(this.hotTrack.id)
                return this.$store.dispatch('playTrackList', {
                    tracks: [song]
                })
            }
            if (this.mobileTracks.length > 0) {
                this.play(0)
            }
        },
        play(index) {
            this.$store.dispatch('playTrackList', {
                index,
                tracks: this.randomSongs
            })
        },
        redirectToAlbums() {
            this.$router.push({path: '/albums/recently-added/'})
        },
        redirectToPlaylists(sort = 'recently-added') {
            this.$router.push({ path: '/playlists/' + sort + '/' })
        },
        playRecentTrack(index) {
            this.$store.dispatch('playTrackList', {
                index,
                tracks: this.recentPlays.map(normalizeTrack)
            })
        },
        redirectToHistory() {
            this.$router.push({ path: '/tracks/recently-played' })
        },
        redirectToSearch() {
            this.$router.push({ path: '/search' })
        },
        redirectToAccount() {
            this.$router.push({ name: 'listener-account' })
        },
        redirectToRecord() {
            this.$router.push({ name: 'record' })
        },
        addFavouriteAlbum(item) {
            if (item.favourite) {
                this.$api.subsonic.removeFavourite(item.id, 'album').then((res) => {
                    item.favourite = false
                })
            } else {
                this.$api.subsonic.addFavourite(item.id, 'album').then((res) => {
                    item.favourite = true
                })
            }
        },
        toggleFavouriteTrack(item) {
            if (item.favourite) {
                this.$api.subsonic.removeFavourite(item.id, 'track').then(() => {
                    item.favourite = false
                })
            } else {
                this.$api.subsonic.addFavourite(item.id, 'track').then(() => {
                    item.favourite = true
                })
            }
        }
    }
}
</script>
<style scoped>
.listener-discover {
    height: calc(100dvh - 12.25rem);
    padding: 0.3rem 0.1rem 0.25rem;
    box-sizing: border-box;
    overflow-y: auto;
    overflow-x: hidden;
}

.listener-hero {
    padding: 0.55rem 0.3rem 0.2rem;
}

.listener-hero__top {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
    margin-bottom: 1rem;
}

.listener-hero__actions {
    display: flex;
    align-items: center;
    gap: 0.55rem;
}

.listener-hero__eyebrow {
    font-size: 0.92rem;
    font-weight: 700;
    color: #94a3b8;
    letter-spacing: 0.04em;
}

.listener-hero__title {
    margin-top: 0.2rem;
    font-size: 2.1rem;
    line-height: 1;
    font-weight: 800;
    color: #111827;
    letter-spacing: -0.04em;
}

.listener-search-button {
    width: 3rem;
    height: 3rem;
    border: 0;
    border-radius: 999px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.9);
    color: #111827;
    box-shadow: 0 12px 30px rgba(148, 163, 184, 0.25);
}

.listener-search-button svg {
    width: 1.25rem;
    height: 1.25rem;
}

.listener-hero__card {
    position: relative;
    min-height: 12.8rem;
    border-radius: 2rem;
    overflow: hidden;
    background-size: cover;
    background-position: center;
    box-shadow: 0 24px 54px rgba(37, 99, 235, 0.16);
}

.listener-hero__overlay {
    position: absolute;
    inset: 0;
    background: linear-gradient(180deg, rgba(255, 255, 255, 0.08), rgba(15, 23, 42, 0.48));
}

.listener-hero__content {
    position: relative;
    z-index: 1;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    min-width: 0;
    min-height: 12.8rem;
    padding: 1.35rem;
    color: #fff;
}

.listener-hero__badge {
    display: inline-flex;
    align-self: flex-start;
    max-width: 100%;
    padding: 0.35rem 0.7rem;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.18);
    backdrop-filter: blur(8px);
    font-size: 0.76rem;
    font-weight: 700;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.listener-hero__track {
    margin-top: auto;
    font-size: 1.9rem;
    line-height: 1;
    font-weight: 800;
    letter-spacing: 0.08em;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.listener-hero__sub {
    margin-top: 0.45rem;
    font-size: 0.8rem;
    color: rgba(255, 255, 255, 0.78);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.listener-hero__play {
    position: absolute;
    right: 1rem;
    bottom: 1rem;
    z-index: 2;
    width: 3.1rem;
    height: 3.1rem;
    border: 0;
    border-radius: 999px;
    background: #fff;
    color: #111827;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 16px 32px rgba(15, 23, 42, 0.24);
}

.listener-hero__play svg {
    width: 1.45rem;
    height: 1.45rem;
}

.listener-section {
    margin-top: 1.35rem;
    padding: 0 0.3rem;
}

.listener-section__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 0.9rem;
}

.listener-section__header h2 {
    font-size: 1.7rem;
    line-height: 1;
    font-weight: 800;
    color: #111827;
    letter-spacing: -0.04em;
}

.listener-section__header button {
    border: 0;
    background: transparent;
    color: #94a3b8;
    font-size: 0.82rem;
    font-weight: 700;
}

.listener-playlist-shortcuts {
    display: flex;
    flex-direction: column;
    gap: 0.85rem;
}

.listener-playlist-shortcuts__hero {
    border: 0;
    width: 100%;
    padding: 1rem;
    border-radius: 1.6rem;
    background:
        radial-gradient(circle at top right, rgba(191, 219, 254, 0.32), transparent 36%),
        linear-gradient(135deg, rgba(255, 255, 255, 0.92), rgba(255, 255, 255, 0.74));
    box-shadow: 0 16px 34px rgba(148, 163, 184, 0.14);
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    text-align: left;
}

.listener-playlist-shortcuts__hero-copy {
    min-width: 0;
    flex: 1;
}

.listener-playlist-shortcuts__eyebrow {
    font-size: 0.74rem;
    font-weight: 700;
    color: #94a3b8;
    letter-spacing: 0.04em;
}

.listener-playlist-shortcuts__title {
    margin-top: 0.3rem;
    font-size: 1.08rem;
    line-height: 1.4;
    font-weight: 800;
    color: #111827;
}

.listener-playlist-shortcuts__sub {
    margin-top: 0.28rem;
    font-size: 0.78rem;
    line-height: 1.55;
    color: #64748b;
}

.listener-playlist-shortcuts__hero-icon {
    width: 3rem;
    height: 3rem;
    border-radius: 1rem;
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: #111827;
    color: #fff;
    box-shadow: 0 12px 22px rgba(15, 23, 42, 0.18);
}

.listener-playlist-shortcuts__hero-icon svg {
    width: 1.3rem;
    height: 1.3rem;
}

.listener-playlist-shortcuts__grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.85rem;
}

.listener-playlist-shortcuts__card {
    border: 0;
    min-height: 4.9rem;
    padding: 0.88rem 0.9rem;
    border-radius: 1.25rem;
    background: rgba(255, 255, 255, 0.74);
    box-shadow: 0 10px 22px rgba(148, 163, 184, 0.1);
    text-align: left;
}

.listener-playlist-shortcuts__card-title {
    font-size: 0.92rem;
    font-weight: 800;
    color: #111827;
}

.listener-playlist-shortcuts__card-sub {
    margin-top: 0.24rem;
    font-size: 0.73rem;
    line-height: 1.5;
    color: #94a3b8;
}

.listener-album-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 0.85rem;
}

.listener-album-card {
    min-width: 0;
}

.listener-album-card__image {
    width: 100%;
    aspect-ratio: 1;
    object-fit: cover;
    border-radius: 1.2rem;
    box-shadow: 0 16px 28px rgba(148, 163, 184, 0.18);
}

.listener-album-card__title,
.listener-album-card__artist,
.listener-artist-card__name,
.listener-track-row__title,
.listener-track-row__artist {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.listener-album-card__title {
    margin-top: 0.6rem;
    font-size: 0.92rem;
    font-weight: 700;
    color: #111827;
}

.listener-album-card__artist {
    margin-top: 0.18rem;
    font-size: 0.75rem;
    color: #94a3b8;
}

.listener-artist-strip {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 0.9rem;
}

.listener-artist-card {
    padding: 0.95rem 0.72rem 0.85rem;
    border-radius: 1.4rem;
    background:
        radial-gradient(circle at top right, rgba(191, 219, 254, 0.32), transparent 42%),
        linear-gradient(180deg, rgba(255, 255, 255, 0.82), rgba(255, 255, 255, 0.68));
    box-shadow: 0 12px 26px rgba(148, 163, 184, 0.12);
    text-align: center;
}

.listener-artist-card__image {
    width: 100%;
    max-width: 4.5rem;
    aspect-ratio: 1;
    margin: 0 auto 0.6rem;
    border-radius: 999px;
    object-fit: cover;
    box-shadow: 0 12px 24px rgba(148, 163, 184, 0.16);
    background: rgba(255, 255, 255, 0.9);
}

.listener-artist-card__name {
    font-size: 0.86rem;
    font-weight: 700;
    color: #111827;
}

.listener-genre-strip {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.9rem;
}

.listener-genre-card {
    border: 0;
    min-height: 4.6rem;
    padding: 0.8rem 0.9rem;
    border-radius: 1.25rem;
    background:
        radial-gradient(circle at top right, rgba(191, 219, 254, 0.3), transparent 40%),
        rgba(255, 255, 255, 0.74);
    box-shadow: 0 10px 22px rgba(148, 163, 184, 0.1);
    text-align: left;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
}

.listener-genre-card__name,
.listener-genre-card__sub {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
}

.listener-genre-card__name {
    font-size: 0.94rem;
    font-weight: 800;
    color: #111827;
    white-space: nowrap;
}

.listener-genre-card__sub {
    margin-top: 0.18rem;
    font-size: 0.72rem;
    color: #94a3b8;
    white-space: nowrap;
}

.listener-track-list {
    padding-bottom: 0.4rem;
}

.listener-track-row {
    display: flex;
    align-items: center;
    gap: 0.85rem;
    width: 100%;
    max-width: 100%;
    padding: 0.85rem 0.5rem;
    margin-bottom: 0.25rem;
    border-radius: 0.75rem;
    overflow: hidden;
    box-shadow: 0 2px 8px rgba(148, 163, 184, 0.08);
    transition: box-shadow 0.2s ease;
    box-sizing: border-box;
}

.listener-track-row:hover {
    box-shadow: 0 4px 12px rgba(148, 163, 184, 0.15);
}

.listener-track-row__cover {
    width: 3.3rem;
    height: 3.3rem;
    border-radius: 1rem;
    overflow: hidden;
    flex-shrink: 0;
}

.listener-track-row__cover img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.listener-track-row__meta {
    min-width: 0;
    flex: 1;
    overflow: hidden;
}

.listener-track-row__title {
    font-size: 0.94rem;
    font-weight: 700;
    color: #111827;
}

.listener-track-row__artist {
    margin-top: 0.18rem;
    font-size: 0.75rem;
    color: #94a3b8;
}

.listener-track-row__side {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 0.45rem;
    flex-shrink: 0;
}

.listener-track-row__duration {
    font-size: 0.74rem;
    color: #94a3b8;
}

.listener-track-row__more {
    width: 2rem;
    height: 2rem;
    border: 0;
    background: transparent;
    color: #cbd5e1;
}

.listener-track-row__more svg {
    width: 1.2rem;
    height: 1.2rem;
}

.body {
    display: flex;
    justify-content: start;
    align-items: center;
}

.shell {
    position: relative;
    display: flex;
    justify-content: center;
    flex-wrap: wrap;
    /* 设置3d变化 */
    transform-style: preserve-3d;
    /* 添加透视效果 */
    perspective: 900px;
}

.shell .box {
    position: relative;
    width: 200px;
    height: 270px;
    transition: 0.3s;
    overflow: hidden;
    margin: 15px;
    transform: rotateY(0deg);
    transition-delay: .1s;
    border-radius: 5px;
    border: #fff 5px solid;
}

/* 当鼠标所有卡片的父元素点shell上时，
所有卡片像Y轴翻转20度 */
.shell:hover .box {
    transform: rotateY(20deg);
}

/* 当鼠标悬浮在当前卡片上时，当前卡片的翻转角度为0，
并放大一点二五倍，加个阴影 */
.shell .box:hover {
    transform: rotateY(0deg) scale(1.25);
    box-shadow: 0 25px 40px rgba(0, 0, 0, 0.7);
    z-index: 1;
}

/* 当鼠标悬浮在卡片上时，
当前卡片后面的所有卡片全部都反向翻转负20度 */
.shell .box:hover~.box {
    transform: rotateY(-20deg);
}

.shell .box .images img {
    width: 100%;
}

.shell .box .content {
    position: absolute;
    top: 0;
    height: 100%;
    z-index: 999;
    padding: 15px;
}

.shell .box .content h2 {
    color: rgb(210, 140, 140);
    transition: 0.3s;
    font-size: 20px;
    transform: translateY(-100px);
}

.shell .box:hover .content h2 {
    transform: translateY(-15px);
}

.shell .box .content p {
    color: rgb(0, 0, 0);
    transition: 0.3s;
    font-size: 14px;
    transform: translateY(600px);
    background-color: rgba(255, 255, 255, 0.7);
}

.shell .box:hover .content p {
    transform: translateY(185px);
}
</style>
